import { createAnalyticsToken } from '@vizit/api';
import { APIError, APIErrorType } from '@vizit/auth';

const EXPIRY_MARGIN_MS = 30_000;

interface CachedToken {
  token: string;
  expiresAt: number;
}

class AnalyticsTokenStore {
  private cached: CachedToken | undefined;
  private inFlight: Promise<string> | undefined;

  getToken(): Promise<string> {
    if (this.cached && Date.now() < this.cached.expiresAt) {
      return Promise.resolve(this.cached.token);
    }

    return this.refresh();
  }

  refresh(): Promise<string> {
    if (!this.inFlight) {
      this.inFlight = this.fetchToken().finally(() => {
        this.inFlight = undefined;
      });
    }

    return this.inFlight;
  }

  private async fetchToken(): Promise<string> {
    const data = await createAnalyticsToken();
    const lifetimeMs = data.expires_in * 1000;

    this.cached = {
      token: data.token,
      expiresAt: Date.now() + lifetimeMs - Math.min(EXPIRY_MARGIN_MS, lifetimeMs / 2),
    };

    return data.token;
  }
}

const isSessionExpiredError = (error: unknown): boolean =>
  error instanceof APIError && error.type === APIErrorType.NotAuthenticated;

export { AnalyticsTokenStore, isSessionExpiredError };
